import { useEffect, useRef } from "react";

import { useStore } from "@/lib/store";
import type { JobStatus, Session } from "@/lib/types";

import { usePreferences } from "./usePreferences";
import { useSessions } from "./useSessions";

const PENDING_STATUSES: JobStatus[] = ["pending", "processing", "awaiting_response"];

function wasPending(status: JobStatus | undefined): boolean {
  return status !== undefined && PENDING_STATUSES.includes(status);
}

function formatNotificationBody(session: Session): string {
  const promptPreview = session.latestMessage.replace(/\s+/g, " ").trim();
  if (promptPreview) {
    return promptPreview.length > 140 ? `${promptPreview.slice(0, 137)}...` : promptPreview;
  }

  return "Your answer is ready.";
}

export function useAnswerReadyNotifications(options: { enabled?: boolean } = {}): void {
  const enabled = options.enabled ?? true;
  const selectSession = useStore((state) => state.selectSession);
  const { data: preferencesData } = usePreferences();
  const notificationsPreference =
    preferencesData?.preferences.webAnswerReadyNotifications ?? "not_asked";

  const { data } = useSessions(
    {
      mine: true,
      source: "web",
      includeAutomated: false,
      limit: 100,
    },
    {
      enabled,
      refetchIntervalInBackground: true,
    },
  );

  const lastStatusByThreadRef = useRef(new Map<string, JobStatus>());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !data) {
      return;
    }

    const sessions = data.sessions.filter((session) => session.source === "web");

    if (!initializedRef.current) {
      for (const session of sessions) {
        lastStatusByThreadRef.current.set(session.threadKey, session.latestStatus);
      }
      initializedRef.current = true;
      return;
    }

    const isNotificationsEnabled = notificationsPreference === "enabled";
    const canNotify =
      isNotificationsEnabled &&
      typeof window !== "undefined" &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted";

    const isActivelyViewingPage = document.visibilityState === "visible" && document.hasFocus();

    for (const session of sessions) {
      const previousStatus = lastStatusByThreadRef.current.get(session.threadKey);
      const nextStatus = session.latestStatus;

      if (
        canNotify &&
        !isActivelyViewingPage &&
        wasPending(previousStatus) &&
        nextStatus === "completed"
      ) {
        const titleText = session.sessionTitle?.trim() || "Sherlog";
        const notification = new Notification(`Answer ready: ${titleText}`, {
          body: formatNotificationBody(session),
          tag: `sherlog-answer:${session.threadKey}`,
        });

        notification.addEventListener("click", () => {
          window.focus();
          selectSession(session.threadKey);
          notification.close();
        });
      }

      lastStatusByThreadRef.current.set(session.threadKey, nextStatus);
    }
  }, [data, enabled, notificationsPreference, selectSession]);
}
